import type { ReceiptData } from '@matix/types';
import { buildZatcaQrSvg } from './zatcaQr.js';
import { qrTimestamp } from './buildA4Html.js';

/**
 * The ZATCA QR for a THERMAL receipt — the built-in slip and the Receipt
 * Designer's "ZATCA QR" block — or null when this tenant is not set up for
 * ZATCA.
 *
 * "Set up for ZATCA" means both:
 *   - a VAT registration number on the company (`mstr_Subsidaries.CivilId`,
 *     carried as `printConfig.taxRegisterNo`), and
 *   - a SAR base currency.
 * The currency half is load-bearing. The VAT number lives in a column named
 * CivilId, which a Kuwaiti or Egyptian tenant may well have filled with
 * something else; a Saudi tax QR appearing on their customers' paper would
 * be wrong, and nothing else about the receipt says which country it is in.
 *
 * The payload is the one the A4 sheet prints, which matches the ERP's QR byte
 * for byte (see buildA4Html.test.ts): seller (Arabic name first), VAT number,
 * local timestamp with no zone, total incl. VAT and VAT, both to 2 decimals.
 */
export function receiptZatcaQrSvg(data: ReceiptData, sizeMm = 30): string | null {
  const pc = data.printConfig;
  const vat = (pc.taxRegisterNo ?? '').trim();
  if (!vat) return null;
  const cur = data.currency ?? pc.currency;
  const code = (typeof cur === 'string' ? cur : cur?.code ?? '').trim().toUpperCase();
  if (code !== 'SAR') return null;
  const seller = (pc.companyNameAr ?? '').trim() || (pc.companyNameEn ?? '').trim();
  const tax = data.totals.tax ?? 0;
  const total = data.totals.total ?? (data.totals.subtotal ?? 0) - (data.totals.discount ?? 0) + tax;
  const svg = buildZatcaQrSvg({
    sellerName: seller,
    vatNumber: vat,
    timestamp: qrTimestamp(data.entryDate),
    total: total.toFixed(2),
    vat: tax.toFixed(2),
    sizeMm,
  });
  return svg || null;
}
